import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { LogOut, ClipboardList, PlusCircle, BarChart3, Users, Package, Truck, Menu, ShoppingCart, PackageCheck, Settings, BellRing, X } from "lucide-react";
import { useState, useEffect } from "react";
import { api } from "@/lib/api";
import NotificationBell from "@/components/NotificationBell";

const linkBase = "flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors";
const linkInactive = "text-slate-600 hover:bg-slate-100";
const linkActive = "bg-slate-900 text-white";

function NavBadge({ count, testId }) {
  if (!count || count <= 0) return null;
  return (
    <span
      data-testid={testId}
      className="ml-auto min-w-[18px] h-[18px] px-1 rounded-full bg-amber-500 text-white text-[10px] font-bold flex items-center justify-center"
    >
      {count > 99 ? "99+" : count}
    </span>
  );
}

export default function Shell({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [counts, setCounts] = useState({ my_purchasing: 0 });
  const [dismissedAt, setDismissedAt] = useState(-1);

  const isAdmin = user && user.role === "admin";
  const isPurchasing = user && (user.role === "admin" || user.role === "purchasing" || (user.extra_roles || []).includes("purchasing"));

  // Poll assigned-purchasing count so the prominent banner/badge stays fresh.
  useEffect(() => {
    if (!user || !user.id) return;
    let active = true;
    const fetchCounts = async () => {
      try {
        const { data } = await api.get("/inbox/counts");
        if (active) setCounts(data);
      } catch (_) { /* offline */ }
    };
    fetchCounts();
    const id = setInterval(fetchCounts, 30_000);
    return () => { active = false; clearInterval(id); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const myPurchasing = counts.my_purchasing || 0;
  const showBanner = myPurchasing > 0 && myPurchasing !== dismissedAt;

  const navItems = [
    { to: "/talepler", label: "Talepler", icon: ClipboardList, testId: "nav-requests" },
    { to: "/yeni-talep", label: "Yeni Talep", icon: PlusCircle, testId: "nav-new-request" },
    ...(isPurchasing ? [{ to: "/satinalma-kutusu", label: "Satın Alma", icon: ShoppingCart, testId: "nav-purchasing", badge: myPurchasing }] : []),
    { to: "/depo-kutusu", label: "Depo Kontrol", icon: PackageCheck, testId: "nav-warehouse" },
    ...(isAdmin ? [{ to: "/raporlar", label: "Raporlar", icon: BarChart3, testId: "nav-reports" }] : []),
    ...(isPurchasing ? [
      { to: "/urunler", label: "Ürünler", icon: Package, testId: "nav-products" },
      { to: "/tedarikciler", label: "Tedarikçiler", icon: Truck, testId: "nav-suppliers" },
    ] : []),
    ...(isAdmin ? [
      { to: "/kullanicilar", label: "Kullanıcılar", icon: Users, testId: "nav-users" },
      { to: "/ayarlar", label: "Ayarlar", icon: Settings, testId: "nav-settings" },
    ] : []),
  ];

  const handleLogout = async () => {
    await logout();
    navigate("/giris", { replace: true });
  };

  return (
    <div className="app-shell flex flex-col min-h-screen">
      {/* Top bar */}
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/85 border-b border-slate-200" style={{ paddingTop: "env(safe-area-inset-top)" }}>
        <div className="max-w-[1850px] mx-auto px-4 sm:px-6 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              data-testid="mobile-menu-btn"
              className="lg:hidden p-2 -ml-2 rounded-md hover:bg-slate-100"
              onClick={() => setOpen(!open)}
              aria-label="Menü"
            >
              <Menu className="h-5 w-5" />
            </button>
            <div className="font-display text-lg font-semibold tracking-tight">
              <span className="text-emerald-700">●</span> SatınAl<span className="text-slate-400">Track</span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <NotificationBell />
            <div className="hidden sm:flex flex-col items-end leading-tight">
              <span data-testid="current-user-name" className="text-sm font-medium text-slate-900">{user?.name}</span>
              <span className="text-xs text-slate-500">{user?.role === "admin" ? "Yönetici" : user?.role === "purchasing" ? "Satınalmacı" : user?.role === "warehouse" ? "Depo Kontrol" : "Kullanıcı"}</span>
            </div>
            <button
              data-testid="logout-btn"
              onClick={handleLogout}
              className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-700 hover:text-slate-900 px-3 h-9 rounded-md hover:bg-slate-100"
            >
              <LogOut className="h-4 w-4" />
              <span className="hidden sm:inline">Çıkış</span>
            </button>
          </div>
        </div>
      </header>

      {/* Prominent global alert: purchase requests forwarded specifically to me */}
      {showBanner && (
        <div data-testid="my-purchasing-banner" className="bg-amber-500 text-white shadow-sm">
          <div className="max-w-[1850px] mx-auto px-4 sm:px-6 py-2.5 flex items-center gap-3">
            <span className="relative flex h-2.5 w-2.5 shrink-0">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75" />
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-white" />
            </span>
            <BellRing className="h-5 w-5 shrink-0" />
            <button
              data-testid="my-purchasing-banner-link"
              onClick={() => navigate("/satinalma-kutusu")}
              className="flex-1 text-left text-sm sm:text-base font-semibold hover:underline"
            >
              Size iletilen {myPurchasing} satın alma talebi var — görüntülemek için dokunun
            </button>
            <button
              data-testid="my-purchasing-banner-view"
              onClick={() => navigate("/satinalma-kutusu")}
              className="hidden sm:inline-flex items-center h-8 px-3 rounded-md bg-white/20 hover:bg-white/30 text-sm font-semibold transition-colors"
            >
              Görüntüle
            </button>
            <button
              data-testid="my-purchasing-banner-dismiss"
              onClick={() => setDismissedAt(myPurchasing)}
              aria-label="Kapat"
              className="p-1 rounded-md hover:bg-white/20 shrink-0 transition-colors"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      <div className="flex-1 flex max-w-[1850px] w-full mx-auto">
        {/* Desktop sidebar */}
        <aside className="hidden lg:flex w-56 shrink-0 flex-col gap-1 p-4 border-r border-slate-200 min-h-[calc(100vh-56px)]">
          {navItems.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              data-testid={it.testId}
              className={({ isActive }) => `${linkBase} ${isActive ? linkActive : linkInactive}`}
            >
              <it.icon className="h-4 w-4" />
              <span>{it.label}</span>
              <NavBadge count={it.badge} testId={`${it.testId}-badge`} />
            </NavLink>
          ))}
        </aside>

        {/* Mobile drawer */}
        {open && (
          <div className="lg:hidden fixed inset-0 z-50">
            <div className="absolute inset-0 bg-black/30" onClick={() => setOpen(false)} />
            <div className="absolute left-0 top-0 bottom-0 w-64 bg-white p-4 flex flex-col gap-1" style={{ paddingTop: "calc(1rem + env(safe-area-inset-top))" }}>
              <div className="font-display text-lg font-semibold tracking-tight pb-3 border-b border-slate-200 mb-2">
                <span className="text-emerald-700">●</span> SatınAl<span className="text-slate-400">Track</span>
              </div>
              {navItems.map((it) => (
                <NavLink
                  key={it.to}
                  to={it.to}
                  onClick={() => setOpen(false)}
                  data-testid={`mobile-${it.testId}`}
                  className={({ isActive }) => `${linkBase} ${isActive ? linkActive : linkInactive}`}
                >
                  <it.icon className="h-4 w-4" />
                  <span>{it.label}</span>
                  <NavBadge count={it.badge} testId={`mobile-${it.testId}-badge`} />
                </NavLink>
              ))}
            </div>
          </div>
        )}

        <main className="flex-1 p-4 sm:p-6 min-w-0">{children}</main>
      </div>

      {/* Mobile bottom nav (only primary) */}
      <nav className="lg:hidden sticky bottom-0 z-30 grid grid-cols-4 bg-white/95 backdrop-blur-xl border-t border-slate-200" style={{ paddingBottom: "env(safe-area-inset-bottom)" }}>
        <NavLink to="/talepler" data-testid="bottom-nav-requests" className={({isActive}) => `flex flex-col items-center justify-center py-2 ${isActive ? "text-slate-900" : "text-slate-500"}`}>
          <ClipboardList className="h-5 w-5" />
          <span className="text-[11px] font-medium mt-0.5">Talepler</span>
        </NavLink>
        <NavLink to="/yeni-talep" data-testid="bottom-nav-new" className={({isActive}) => `flex flex-col items-center justify-center py-2 ${isActive ? "text-slate-900" : "text-slate-500"}`}>
          <PlusCircle className="h-5 w-5" />
          <span className="text-[11px] font-medium mt-0.5">Yeni</span>
        </NavLink>
        <NavLink to="/depo-kutusu" data-testid="bottom-nav-warehouse" className={({isActive}) => `flex flex-col items-center justify-center py-2 ${isActive ? "text-slate-900" : "text-slate-500"}`}>
          <PackageCheck className="h-5 w-5" />
          <span className="text-[11px] font-medium mt-0.5">Depo</span>
        </NavLink>
        <NavLink to={isAdmin ? "/raporlar" : "/talepler"} data-testid="bottom-nav-reports" className={({isActive}) => `flex flex-col items-center justify-center py-2 ${isActive ? "text-slate-900" : "text-slate-500"}`}>
          <BarChart3 className="h-5 w-5" />
          <span className="text-[11px] font-medium mt-0.5">{isAdmin ? "Rapor" : "Özet"}</span>
        </NavLink>
      </nav>
    </div>
  );
}
